import { BrowserRouter, Routes, Route } from 'react-router-dom'
import { KeycloakProvider } from './auth/KeycloakProvider'
import { useAuth } from './auth/KeycloakProvider'
import { ProtectedRoute } from './auth/ProtectedRoute'
import { RoleBasedRedirect } from './components/RoleBasedRedirect'
import './index.css'
import './App.css'

// Lazy load pages for better code splitting
import { lazy, Suspense } from 'react'

const Dashboard = lazy(() => import('./pages/Dashboard'))
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'))
const NoAccessPage = lazy(() => import('./pages/NoAccessPage'))
const NotFound = lazy(() => import('./pages/NotFound'))

function LoadingFallback() {
    return (
        <div className="loading-container">
            <div className="loading-spinner" />
            <p>Loading...</p>
        </div>
    )
}

/**
 * Extracts initials from a name or email.
 * - "John Doe" → "JD"
 * - "john.doe@variphy.com" → "J"
 * - undefined → ""
 */
function getInitials(name?: string, email?: string): string {
    if (name) {
        const parts = name.trim().split(/\s+/)
        return parts
            .slice(0, 2)
            .map((p) => p[0]?.toUpperCase() ?? '')
            .join('')
    }
    if (email) {
        return email[0]?.toUpperCase() ?? ''
    }
    return ''
}

function AppHeader() {
    const { userName, userEmail } = useAuth()

    const displayName = userName || undefined
    const initials = getInitials(userName ?? undefined, userEmail ?? undefined)

    return (
        <header className="app-header">
            <div className="app-header__brand">
                <div className="app-header__logo" aria-hidden="true">V</div>
                <div>
                    <h1 className="app-header__title">Variphy Admin Portal</h1>
                    <p className="app-header__subtitle">Access Grant Management</p>
                </div>
            </div>
            {(displayName || initials) && (
                <div className="app-header__user">
                    <div className="app-header__avatar" aria-hidden="true">{initials}</div>
                    {displayName && (
                        <span className="app-header__user-name">{displayName}</span>
                    )}
                </div>
            )}
        </header>
    )
}

function App() {
    return (
        <KeycloakProvider>
            <BrowserRouter>
                <div className="app">
                    <AppHeader />
                    <main className="app-main">
                        <Suspense fallback={<LoadingFallback />}>
                            <Routes>
                                <Route path="/" element={<RoleBasedRedirect />} />
                                <Route
                                    path="/dashboard"
                                    element={
                                        <ProtectedRoute requiredRoles={['support_engineer', 'admin']}>
                                            <Dashboard />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/admin"
                                    element={
                                        <ProtectedRoute requiredRoles={['admin']}>
                                            <AdminDashboard />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route path="/no-access" element={<NoAccessPage />} />
                                <Route path="*" element={<NotFound />} />
                            </Routes>
                        </Suspense>
                    </main>
                    <footer className="app-footer">
                        <p>&copy; 2026 Variphy, Inc.</p>
                    </footer>
                </div>
            </BrowserRouter>
        </KeycloakProvider>
    )
}

export default App
